/* eslint-disable @typescript-eslint/no-explicit-any */
import { getDatabaseAnchor } from '@common/utils/flow';
import { AnchorType, FullEdge } from '@components/Flow';
import { config, ModNodeState, ModuleParameter, NodeFieldType, NodeInputType } from '@components/Flow/Nodes';
import { getWorkflowData, WorkflowDataParams } from '@requests/flow';
import { Command, State } from '@store/index';
import { defaultActiveTab, Tab, tabActions } from '@store/slices/tabs';
import { UseMutateAsyncFunction, useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Viewport, useReactFlow } from 'reactflow';
import { DatabaseEdge, DatabaseNode, WorkflowData } from './types';
import { RequestError } from '@requests/common';

type WorkflowHook = {
  status: string;
  getWorkflow: (workflow_id: string, label?: string) => void;
  loadFlowIntoStore: (data: WorkflowData | { error: string }) => void;
  refreshModules: UseMutateAsyncFunction<WorkflowData, Error, any, unknown>;
};

export interface LoadTabProperties extends Tab {
  flow_id: string;
  label: string;
  nodeIndex: number;
  edgeIndex: number;
  flow: WorkflowData['flow'];
  edges: WorkflowData['edges'];
  commands: Command[];
  service_id: string;
  service_path: string;
  viewport: Viewport;
}

export const useWorkflow = (): WorkflowHook => {
  const dispatch = useDispatch();
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const theme = useSelector((state: State) => state.flow.theme);
  const tabs = useSelector((state: State) => state.tabs.list);

  const { getViewport } = useReactFlow();
  const { service_id } = useParams();

  const { mutateAsync: load, status } = useMutation<WorkflowData, RequestError, WorkflowDataParams>({
    mutationFn: getWorkflowData,
    onMutate: (data) =>
      dispatch(
        tabActions.createLoadingTab({
          workflow_id: data.workflow_id,
          label: data?.label || '',
          viewport: getViewport(),
        })
      ),
    onSuccess: (data) => loadFlowIntoStore(data),
    onError: (error) => {
      dispatch(tabActions.setError(error?.response?.data?.error || error.message));
    },
  });

  const { mutateAsync: refreshModules } = useMutation({
    mutationFn: getWorkflowData,
    onSuccess: (data) => loadModulesIntoStore(data),
  });

  const getWorkflow = async (workflow_id: string, label?: string) => {
    if (!workflow_id) return;

    dispatch(tabActions.setIdentifier(workflow_id));
    const tab = tabs.find((tab) => tab.flow_id === workflow_id);

    // if flow is already loaded into a tab
    if (tab) {
      return dispatch(tabActions.setCurrent(workflow_id));
    }

    load({ label: label || workflow_id, workflow_id, service_id }).catch(() => null);
  };

  const loadModulesIntoStore = (data: WorkflowData): void => {
    if (!data?.flow) return;

    try {
      const parentModule = translateDatabaseWorkflow(data);
      dispatch(tabActions.updateTabModules({ moduleId: parentModule.flow.flow_id, tab: parentModule }));
    } catch (error: unknown) {
      console.error(error);
    }
  };

  const loadFlowIntoStore = (data: WorkflowData | any): void => {
    if (data.hasOwnProperty('error')) {
      dispatch(tabActions.setError(data?.error));
      return;
    }

    if (!data?.flow) return;

    try {
      const parentModules = [];
      const parentWorkflowTab = translateDatabaseWorkflow(data);
      const modules = data?.modules || [];

      for (const mod of modules) {
        const translatedModule = translateDatabaseWorkflow(mod);
        parentModules.push(translatedModule);
      }

      const tabWithModules = {
        ...parentWorkflowTab,
        modules: parentModules,
      };

      dispatch(tabActions.createTab(tabWithModules));
    } catch (error: unknown) {
      const defaultError = 'An unexpected error occured while trying to load this flow.';

      if (error instanceof Error) {
        dispatch(tabActions.setError(error.message));
      } else {
        dispatch(tabActions.setError(defaultError));
      }

      console.error(error);
    }
  };

  const translateDatabaseWorkflow = (data: WorkflowData): LoadTabProperties => {
    const flow_id = data.flow.flow_id;
    const commands = getCommands(data);
    const edges = getEdges(data);

    const label = data.flow.name;
    const maxNodeIndex = Math.max(...commands.map((command) => Number.parseInt(command.node.id)));
    const nodeIndex = maxNodeIndex + 1;
    const edgeIndex = edges.length + 2;

    const tabProperties: LoadTabProperties = {
      ...defaultActiveTab,
      flow_id,
      label,
      nodeIndex,
      edgeIndex,
      flow: {
        ...data.flow,
        data_prefixes: data?.flow?.data_prefixes || {},
        authentication: data.flow?.authentication || {
          OPENAI_API_KEY: null,
          GOOGLE_API_KEY: null,
          ANTHROPIC_API_KEY: null,
          GROK_API_KEY: null,
          GROQ_API_KEY: null,
        },
      },
      edges,
      commands,
      service_id: data.flow.service_id,
      service_path: data.flow.service_path,
      viewport: getViewport(),
    };

    return tabProperties;
  };

  // convert database edges to workflow edges
  const getEdges = (data: WorkflowData) => {
    const edges =
      data.edges.map((edge: any) => {
        if (edge?.variant?.includes('execution') && theme === 'light') {
          return {
            ...edge,
            style: {
              stroke: '#000000',
              strokeWidth: 2,
              strokeDasharray: edge?.variant === 'conditional_execution' ? '12,12' : undefined,
            },
          };
        }

        if (edge.variant === 'data' && theme === 'light') {
          return {
            ...edge,
            style: {
              stroke: '#3434EF',
              strokeWidth: 2,
            },
          };
        }

        if (edge.variant === 'data' && theme === 'dark') {
          return {
            ...edge,
            style: {
              stroke: '#5858FF',
              strokeWidth: 2,
            },
          };
        }

        if (edge?.variant?.includes('execution') && theme === 'dark') {
          return {
            ...edge,
            style: {
              strokeWidth: 2,
              stroke: '#ffffff',
              strokeDasharray: edge?.variant === 'conditional_execution' ? '12,12' : undefined,
            },
          };
        }

        return edge;
      }) || [];

    return edges;
  };

  // convert database nodes to workflow commands
  const getCommands = (data: WorkflowData): Command[] => {
    const edges = data.edges || [];

    return data.nodes.map((node): Command => {
      const node_config = config?.[node.type as string];

      if (!node_config) {
        const availableTypes = Object.keys(config).join(', ');
        throw new Error(
          `Failed to find configuration file for node type: ${node.type}.\n` +
            `The only available configurations found are: ${availableTypes}.\n` +
            `This is a fatal error, please report this issue to support.`
        );
      }

      const fields = getNodeFields(node, data.modules);
      const state = getNodeState(node, data.modules);
      const connections = getNodeConnections(node.node_id, edges);
      const output = getNodeOutput(node);

      return {
        node: {
          id: node.node_id,
          width: node.width,
          type: node.type,
          data: {
            targets: edges.filter((edge) => edge.source === node.node_id),
            sources: edges.filter((edge) => edge.target === node.node_id),
          },
          style: {
            width: node.width,
          },
          position: {
            x: node.positionx,
            y: node.positiony,
          },
        },
        config: node_config.config,
        connections,
        fields,
        state,
        output,
      };
    });
  };

  const getNodeOutput = (node: DatabaseNode): any => {
    const node_config = config?.[node.type as string];

    const isWebhookNode = node_config?.config?.anchors?.some(
      (anchor: AnchorType) => anchor?.variant === 'webhook_execution'
    );

    // required to avoid err: Cannot assign to read only property 'dynamic' of object '#<Object>'
    const _output = node.output || node_config?.output;
    const output = { ..._output };

    if (node_config?.output?.default) output.default = node_config.output.default;

    if (isWebhookNode) {
      try {
        const dynamicOutput = node?.output?.dynamic || node?.dynamicOutput;
        const webhookKeys = dynamicOutput ? Object.keys(dynamicOutput) : [];
        const allOutputs = webhookKeys.map((webhookKey) => dynamicOutput?.[webhookKey]);

        const hasAllWebhooks = allOutputs.every((item) => {
          const outputKeys = Object.keys(item);
          return outputKeys.length > 0;
        });

        if (hasAllWebhooks && webhookKeys.length > 0) {
          output.dynamic = dynamicOutput;
        } else output.dynamic = null;

        return output;
      } catch (e) {
        return output;
      }
    }

    try {
      const dynamicOutput = node?.output?.dynamic;
      const hasKeys = dynamicOutput && Object.keys(dynamicOutput).length > 0;

      if (dynamicOutput && hasKeys) output.dynamic = dynamicOutput;
      else output.dynamic = null;
    } catch (e) {
      return output;
    }

    return output;
  };

  const getNodeState = (node: DatabaseNode, modules: Omit<WorkflowData, 'modules'>[] | undefined = []): any => {
    const node_config = config?.[node.type as string];

    if (!node.attributes.length) return node_config?.state;

    const default_state = node_config.state || {};
    const storedState = node.attributes.reduce((acc, attribute) => {
      acc[attribute.id] = attribute.value;
      return acc;
    }, {} as { [key: string]: string });

    const state = { ...default_state, ...storedState };

    if (node.command_id === 'node.module') {
      const modTriggerNode = getModuleTriggerNode(node, modules);
      if (modTriggerNode) {
        const parameters = modTriggerNode.attributes.find((param) => param.id === 'parameters')?.value || [];
        const newState: ModNodeState = { moduleId: state.moduleId };

        parameters.forEach((param: ModuleParameter) => {
          newState[param.id] = {
            type: NodeInputType.Expression,
            value: '',
          };
        });

        return { ...newState, ...state };
      }
    }

    return state;
  };

  const getNodeFields = (node: DatabaseNode, modules: Omit<WorkflowData, 'modules'>[] | undefined = []) => {
    const node_config = config?.[node.type as string];

    if (node.command_id === 'node.module') {
      const modTriggerNode = getModuleTriggerNode(node, modules);
      if (modTriggerNode) {
        const parameters = modTriggerNode.attributes.find((param) => param.id === 'parameters')?.value || [];
        const fields = node_config?.fields || [];

        const modFields = parameters.map((param: ModuleParameter) => ({
          id: param.id,
          tab: '1',
          type: NodeFieldType.InputExpression,
          value: '',
          label: param.name,
          description: param.description,
          required: param.required,
          placeholder: param.description,
        }));

        return fields.concat(modFields);
      }
    }

    if (node.type === 'JumpTo') {
      return node.attributes.length ? node.attributes : node_config?.fields || [];
    }

    return node_config?.fields;
  };

  /**
   * Used to get the trigger node inside the module of a Module Node.
   * @param node The module node in the parent workflow.
   * @param modules The list of modules used in the workflow.
   * @returns The trigger node used inside the module node.
   */

  const getModuleTriggerNode = (
    node: DatabaseNode,
    modules: Omit<WorkflowData, 'modules'>[] | undefined = []
  ): DatabaseNode | undefined => {
    try {
      const nodeModuleIdAttribute = node?.attributes?.find((attribute) => attribute.id === 'moduleId');
      const nodeModuleId = nodeModuleIdAttribute?.value;
      const modWorkflow = modules?.find((modWorkflow) => modWorkflow.flow.flow_id === nodeModuleId);
      const modTriggerNode = modWorkflow?.nodes?.find((modWorkflowNode) => modWorkflowNode.type === 'Module');
      return modTriggerNode;
    } catch {
      return undefined;
    }
  };

  // get the edge connections for a given node
  const getNodeConnections = (nodeId: string, edges: DatabaseEdge[]): FullEdge[] => {
    return edges
      .filter((edge) => edge.source === nodeId || edge.target === nodeId)
      .map((connection) => {
        return {
          edge: {
            id: connection.id,
            source: connection.source,
            target: connection.target,
            sourceHandle: connection.sourceHandle,
            targetHandle: connection.targetHandle,
          },
          source_anchor: getDatabaseAnchor(connection.sourceHandle, connection, commands) as AnchorType,
          target_anchor: getDatabaseAnchor(connection.targetHandle, connection, commands) as AnchorType,
        };
      });
  };

  return { getWorkflow, status, refreshModules, loadFlowIntoStore };
};
